/*
 * pure rules for the base rate history pop-up and the rate-change line. I/O-free: validation, the
 * pre-save preview and every label are unit-tested in logic.test.js.
 */
import { MONTH_SHORT, fmt } from '../format';
import { ist_month_key } from '../shared/ist';

export const NOTE_MIN = 10;
export const NOTE_MAX = 500;

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const parse_day = (s) => {
  const m = typeof s === 'string' ? s.match(DAY_RE) : null;
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  // Round-trip through UTC rejects 2026-02-30 and friends.
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d)
    return null;
  return { y, mo, d };
};

// '2026-10-15' -> '15 Oct 2026' (or '15 Oct' without the year).
export const day_label = (s, with_year = true) => {
  const p = parse_day(s);
  if (!p) return s || '';
  return with_year ? `${p.d} ${MONTH_SHORT[p.mo - 1]} ${p.y}` : `${p.d} ${MONTH_SHORT[p.mo - 1]}`;
};

// '2026-10' -> 'Oct 2026'.
export const month_short = (key) => {
  const m = typeof key === 'string' ? key.match(/^(\d{4})-(\d{2})/) : null;
  if (!m) return key || '';
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx < 12 ? `${MONTH_SHORT[idx]} ${m[1]}` : key;
};

// created_at_ist '2026-10-03 21:40' -> '3 Oct 2026, 21:40'.
export const stamp_label = (s) => {
  const m = typeof s === 'string' ? s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/) : null;
  return m ? `${day_label(m[1])}, ${m[2]}` : s || '';
};

export const STATUS_VIEW = {
  CURRENT: { label: 'Current', tone: 'green' },
  SCHEDULED: { label: 'Scheduled', tone: 'blue' },
  PAST: { label: 'Past', tone: 'gray' },
  SUPERSEDED: { label: 'Superseded', tone: 'orange' }
};

export const status_view = (status) =>
  STATUS_VIEW[status] || { label: status || '—', tone: 'gray' };

export const is_opening = (entry) => !!entry && entry.source === 'OPENING';

// ---- validation ----

// Whole rupees above 0, typed as digits only ('900', not '900.0' or '9e2').
export const parse_rate = (raw) => {
  const s = String(raw === null || raw === undefined ? '' : raw).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

// Code points, as the server counts runes (utf8.RuneCountInString): an emoji is 1, not 2 units.
export const note_length = (note) => (typeof note === 'string' ? [...note.trim()].length : 0);

// Field -> message for each invalid field; an empty field reads as missing, not wrong.
export const form_errors = (form, bounds) => {
  const f = form || {};
  const b = bounds || {};
  const errors = {};
  if (String(f.rate || '').trim() === '') errors.rate = 'Enter the new rate.';
  else if (parse_rate(f.rate) === null) errors.rate = 'Enter whole rupees above 0.';

  if (!f.effective_date) errors.effective_date = 'Choose the effective date.';
  else if (!parse_day(f.effective_date)) errors.effective_date = 'Enter a valid date.';
  else if (b.earliest_allowed_date && f.effective_date < b.earliest_allowed_date) {
    errors.effective_date = `The earliest allowed date is ${day_label(b.earliest_allowed_date)}.`;
  } else if (b.latest_allowed_date && f.effective_date > b.latest_allowed_date) {
    errors.effective_date = `The latest allowed date is ${day_label(b.latest_allowed_date)}.`;
  }

  const len = note_length(f.note);
  if (len === 0) errors.note = 'Add a note.';
  else if (len < NOTE_MIN) errors.note = `The note needs at least ${NOTE_MIN} characters.`;
  else if (len > NOTE_MAX) errors.note = `The note can be at most ${NOTE_MAX} characters.`;
  return errors;
};

export const can_save = (form, bounds) => Object.keys(form_errors(form, bounds)).length === 0;

export const request_body = (teacher_uuid, form) => ({
  teacher_uuid,
  base_rate: parse_rate(form.rate),
  effective_date: form.effective_date,
  note: form.note.trim()
});

// ---- preview ----

// Winner order: newest effective date first, then latest created. Stable, so exact created_at ties
// keep the server's order (it breaks them by id).
const by_winner = (a, b) => {
  if (a.effective_from_ist !== b.effective_from_ist) {
    return a.effective_from_ist < b.effective_from_ist ? 1 : -1;
  }
  const ca = a.created_at_ist || '';
  const cb = b.created_at_ist || '';
  if (ca === cb) return 0;
  return ca < cb ? 1 : -1;
};

const usable = (entries) =>
  (Array.isArray(entries) ? entries : []).filter((e) => e && parse_day(e.effective_from_ist));

// The rate in effect just before 00:00 IST on `day` (null when nothing was in effect).
export const rate_before = (entries, day) => {
  const hit = usable(entries)
    .filter((e) => e.effective_from_ist < day)
    .sort(by_winner)[0];
  return hit ? hit.base_rate : null;
};

// The earliest later change that will end the new rate's run, if any.
export const next_change_after = (entries, day) => {
  const later = usable(entries).filter((e) => e.effective_from_ist > day);
  if (!later.length) return null;
  const first_day = later.map((e) => e.effective_from_ist).sort()[0];
  return later.filter((e) => e.effective_from_ist === first_day).sort(by_winner)[0];
};

// Months the save is expected to touch: the effective month through the current IST month. The server
// only recalculates OPEN months that exist, and returns the authoritative list after the save.
export const expected_months = (day, now = new Date()) => {
  const p = parse_day(day);
  if (!p) return [];
  const start = `${p.y}-${String(p.mo).padStart(2, '0')}`;
  const end = ist_month_key(now);
  const out = [start];
  let [y, m] = [p.y, p.mo];
  // Bounded: the date picker caps the range at one year either side.
  while (out.length < 36) {
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (key > end) break;
    out.push(key);
  }
  return out;
};

// The line shown above Save; '' until the rate and the date are valid.
export const preview_text = (entries, form, now = new Date()) => {
  const rate = parse_rate(form && form.rate);
  const day = form && form.effective_date;
  if (rate === null || !parse_day(day)) return '';
  const parts = [`Classes from ${day_label(day)}, 00:00 IST use ${fmt(rate)}`];
  const next = next_change_after(entries, day);
  if (next)
    parts[0] += ` until ${day_label(next.effective_from_ist)}, when ${fmt(next.base_rate)} starts`;
  const before = rate_before(entries, day);
  let text = before === null ? `${parts[0]}.` : `${parts[0]}; earlier classes keep ${fmt(before)}.`;
  const months = expected_months(day, now).map(month_short);
  text += ` These months will be recalculated: ${months.join(', ')}.`;
  return text;
};

// ---- refusals ----

const CODE_TEXT = {
  INVALID_RATE: 'Enter the rate as whole rupees above 0.',
  INVALID_DATE: 'Enter a valid effective date.',
  DATE_BEFORE_ALLOWED: 'The effective date falls in a closed month.',
  DATE_TOO_FAR: 'The effective date is more than a year ahead.',
  NOTE_REQUIRED: `Add a note of ${NOTE_MIN} to ${NOTE_MAX} characters.`,
  TEACHER_NOT_FOUND: 'This teacher was not found.',
  FORBIDDEN: 'You do not have permission to change base rates.'
};

const is_code = (s) => /^[A-Z][A-Z0-9_]*$/.test(s);
const text_of = (v) => (typeof v === 'string' ? v.trim() : '');

// The server puts the sentence in `reason` and the code in `error_code`; other routes in this
// module put the sentence in `message`. Read either, never show a bare code.
export const refusal_text = (err, fallback = 'Could not save the rate change. Try again.') => {
  const body = (err && err.response && err.response.data) || {};
  const reason = text_of(body.reason);
  const message = text_of(body.message);
  if (reason && !is_code(reason)) return reason;
  if (message && !is_code(message)) return message;
  const code = text_of(body.error_code) || reason;
  if (CODE_TEXT[code]) return CODE_TEXT[code];
  const status = err && err.response && err.response.status;
  if (status === 403) return CODE_TEXT.FORBIDDEN;
  return fallback;
};

// ---- save result ----

export const saved_text = (result, form) => {
  const r = result || {};
  const months = (Array.isArray(r.recalc_months) ? r.recalc_months : []).map(month_short);
  const rate = fmt(parse_rate(form && form.rate));
  const when = day_label(form && form.effective_date);
  let text = r.applied_now
    ? `Saved. ${rate} is the rate from ${when}.`
    : `Saved. ${rate} takes effect on ${when}.`;
  text += months.length
    ? ` Marked for recalculation: ${months.join(', ')}.`
    : ' No open month needed recalculation.';
  return text;
};

// ---- header ----

// 800 + [{effective_from_ist:'2026-10-15', base_rate:900}] -> '₹800 → ₹900 from 15 Oct'.
export const rate_changes_text = (start_rate, changes) => {
  const list = usable(changes)
    .slice()
    .sort((a, b) => (a.effective_from_ist < b.effective_from_ist ? -1 : 1));
  // A change on the 1st is already the snapshot (the rate on the month's first day): skip no-op steps.
  let prev = Number(start_rate);
  const steps = [];
  list.forEach((c) => {
    if (Number(c.base_rate) === prev) return;
    steps.push(`${fmt(c.base_rate)} from ${day_label(c.effective_from_ist, false)}`);
    prev = Number(c.base_rate);
  });
  if (!steps.length) return '';
  return [fmt(start_rate), ...steps].join(' → ');
};
